import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ExternalLink,
  ArrowLeft,
  ChevronRight,
  Megaphone,
  LayoutTemplate,
  Building2,
  Briefcase,
  Images,
  Network,
  Monitor,
  Tags,
  Download,
  MessageCircle,
  Share2,
  Plus,
  Trash2,
  Check,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  type LucideIcon,
} from 'lucide-react'
import { useCan } from '@/features/auth'
import {
  useLandingContent,
  useLandingContentStore,
  newId,
  DOWNLOAD_PLATFORM_LABELS,
  type LandingContent,
  type DownloadPlatform,
} from '@/features/admin/content/landingContent'
import { ImageField } from '@/features/admin/components/ImageField'
import { AdminContentGate } from '@/features/admin/content/AdminContentGate'

type SectionKey = keyof LandingContent

/** Íconos de tipos de negocio y del ecosistema: PNG o WebP. Antes eran SVG,
 *  pero portal-server ya no admite SVG en la subida (goal seguridad-remediacion
 *  12); los SVG que ya están subidos se siguen viendo dentro de `<img>`. */
const ICON_ACCEPT = '.png,.webp,image/png,image/webp'

/** Secciones estáticas del sitio: sin contenido editable, pero pueden
 *  ocultarse/mostrarse (vía el mapa `hidden`, que admite claves libres). */
const STATIC_SECTIONS = ['hardware'] as const
type StaticSectionKey = (typeof STATIC_SECTIONS)[number]
type AdminSectionKey = SectionKey | StaticSectionKey

const isStaticSection = (key: AdminSectionKey): key is StaticSectionKey =>
  (STATIC_SECTIONS as readonly string[]).includes(key)

const SECTIONS: { key: AdminSectionKey; title: string; icon: LucideIcon; desc: string }[] = [
  { key: 'announcement', title: 'Barra de anuncio', icon: Megaphone, desc: 'Aviso superior del sitio' },
  { key: 'hero', title: 'Sección principal (Hero)', icon: LayoutTemplate, desc: 'Insignia y video "Ver demo"' },
  { key: 'growingBusinesses', title: 'Negocios que crecen', icon: Building2, desc: 'Logos y nombres' },
  { key: 'businessTypes', title: 'Tipos de negocio', icon: Briefcase, desc: 'Categorías con imagen y enlace' },
  { key: 'carousel', title: 'Carrusel', icon: Images, desc: 'Imágenes a todo lo ancho' },
  { key: 'ecosystem', title: 'Ecosistema TecoPOS', icon: Network, desc: 'Módulos con descripción y botón' },
  { key: 'hardware', title: 'Hardware', icon: Monitor, desc: 'Sección estática de equipos compatibles' },
  { key: 'pricing', title: 'Precios', icon: Tags, desc: 'Planes y plan más popular' },
  { key: 'downloads', title: 'Aplicaciones para descargar', icon: Download, desc: 'Tarjetas de /descargas: tiendas, APK e instalador' },
  { key: 'contact', title: 'Contacto (WhatsApp)', icon: MessageCircle, desc: 'Número institucional y mensaje' },
  { key: 'social', title: 'Redes sociales', icon: Share2, desc: 'Canal de WhatsApp, YouTube, Facebook e Instagram' },
]

export function LandingContentPage() {
  const canEdit = useCan('landing:edit')
  const content = useLandingContent()
  const update = useLandingContentStore((s) => s.update)
  const saveSection = useLandingContentStore((s) => s.saveSection)
  const savingSection = useLandingContentStore((s) => s.savingSection)
  const dirty = useLandingContentStore((s) => s.dirty)
  const error = useLandingContentStore((s) => s.error)
  const status = useLandingContentStore((s) => s.status)
  const [active, setActive] = useState<AdminSectionKey | null>(null)

  if (!canEdit) {
    return (
      <div className="rounded-card border border-line bg-panel p-8 text-center">
        <h2 className="text-lg font-semibold text-ink">Acceso restringido</h2>
        <p className="mt-1 text-sm text-ink-muted">Esta sección es exclusiva del administrador del portal.</p>
      </div>
    )
  }

  // Editar sin contenido real pisaría la BD con los defaults al guardar.
  if (status !== 'ready') return <AdminContentGate status={status} />

  const count = (key: AdminSectionKey): number | null => {
    if (isStaticSection(key)) return null
    const v = content[key]
    return Array.isArray(v) ? v.length : null
  }

  // ----- ÍNDICE -----
  if (active === null) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-widest text-brand">Administración del portal</span>
            <h1 className="mt-1 text-2xl font-bold text-ink">Contenido del landing</h1>
            <p className="mt-1 text-sm text-ink-muted">Elige una sección para gestionarla. Cada sección se guarda por separado.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" target="_blank" className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-panel px-3 py-2 text-sm font-medium text-ink hover:bg-surface">
              <ExternalLink className="size-4" /> Ver landing
            </Link>
          </div>
        </header>

        {error && (
          <p className="inline-flex items-center gap-1.5 rounded-lg bg-danger-soft px-3 py-2 text-xs font-medium text-danger">
            <AlertCircle className="size-4" /> {error}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {SECTIONS.map((s) => {
            const Icon = s.icon
            const n = count(s.key)
            return (
              <button
                key={s.key}
                onClick={() => setActive(s.key)}
                className="group flex items-center gap-3 rounded-card border border-line bg-panel p-4 text-left shadow-sm hover:border-brand/40 hover:shadow-md"
              >
                <span className="grid size-10 place-items-center rounded-xl bg-brand-soft text-brand">
                  <Icon className="size-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-sm font-semibold text-ink">{s.title}</h2>
                    {!isStaticSection(s.key) && dirty[s.key] && (
                      <span className="rounded-full bg-warning-soft px-1.5 py-0.5 text-[9px] font-bold uppercase text-warning">Sin guardar</span>
                    )}
                    {(s.key === 'announcement' ? !content.announcement.enabled : content.hidden?.[s.key]) && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-surface px-1.5 py-0.5 text-[9px] font-bold uppercase text-ink-subtle"><EyeOff className="size-2.5" /> Oculta</span>
                    )}
                  </div>
                  <p className="truncate text-xs text-ink-muted">
                    {s.desc}
                    {n !== null && ` · ${n} ${n === 1 ? 'elemento' : 'elementos'}`}
                  </p>
                </div>
                <ChevronRight className="size-4 text-ink-subtle group-hover:text-brand" />
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  // ----- DETALLE DE SECCIÓN -----
  const section = SECTIONS.find((s) => s.key === active)!
  const isDirty = !isStaticSection(active) && !!dirty[active]
  const isSaving = !isStaticSection(active) && savingSection === active

  // Visibilidad de la sección en el sitio público. El anuncio usa su propio `enabled`.
  const sectionVisible =
    active === 'announcement' ? content.announcement.enabled : !content.hidden?.[active]
  const setSectionVisible = (v: boolean) => {
    if (active === 'announcement') {
      update('announcement', { ...content.announcement, enabled: v })
      void saveSection('announcement')
    } else {
      update('hidden', { ...(content.hidden ?? {}), [active]: !v })
      void saveSection('hidden')
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 pb-24">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => setActive(null)} className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-muted hover:text-ink">
          <ArrowLeft className="size-4" /> Secciones
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold text-ink">{section.title}</h1>
          <button
            type="button"
            onClick={() => setSectionVisible(!sectionVisible)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              sectionVisible
                ? 'border-success/30 bg-success-soft text-success'
                : 'border-line bg-surface text-ink-subtle'
            }`}
            title={sectionVisible ? 'Visible en el sitio — clic para ocultar' : 'Oculta — clic para mostrar'}
          >
            {sectionVisible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
            {sectionVisible ? 'Visible' : 'Oculta'}
          </button>
        </div>
      </header>

      <div className="space-y-4">{renderSection(active)}</div>

      {/* Barra de guardado por sección (fija). Las secciones estáticas no
          tienen contenido que guardar: su visibilidad se guarda al instante. */}
      {!isStaticSection(active) && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-panel/95 backdrop-blur">
          <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
            {error ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-danger"><AlertCircle className="size-4" /> {error}</span>
            ) : isDirty ? (
              <span className="text-xs font-medium text-ink-muted">Cambios sin guardar en esta sección.</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success"><Check className="size-4" /> Sección guardada.</span>
            )}
            <button
              onClick={() => void saveSection(active)}
              disabled={isSaving || !isDirty}
              className="ml-auto inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
            >
              {isSaving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
              {isSaving ? 'Guardando…' : 'Guardar sección'}
            </button>
          </div>
        </div>
      )}
    </div>
  )

  // ===================== editores por sección =====================
  function renderSection(key: AdminSectionKey) {
    const { announcement, hero, growingBusinesses, businessTypes, carousel, ecosystem, pricing, downloads, contact, social } = content

    if (isStaticSection(key)) {
      return (
        <div className="rounded-card border border-line bg-panel p-6 text-sm text-ink-muted">
          Esta sección del sitio es estática: su contenido no se edita desde la
          administración. Usa el botón «Visible / Oculta» de arriba para
          mostrarla u ocultarla en el landing — el cambio se guarda al instante
          y también retira sus enlaces del menú y del pie.
        </div>
      )
    }

    switch (key) {
      case 'announcement':
        return (
          <>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={announcement.enabled} onChange={(e) => update('announcement', { ...announcement, enabled: e.target.checked })} className="size-4 accent-[color:var(--brand-orange)]" />
              <span className="text-sm text-ink">Mostrar barra de anuncio en el landing</span>
            </label>
            <Field label="Texto del anuncio" value={announcement.text} onChange={(v) => update('announcement', { ...announcement, text: v })} />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Texto del botón" value={announcement.ctaLabel} onChange={(v) => update('announcement', { ...announcement, ctaLabel: v })} />
              <Field label="Enlace del botón" value={announcement.ctaHref} onChange={(v) => update('announcement', { ...announcement, ctaHref: v })} />
            </div>
          </>
        )

      case 'hero':
        return (
          <>
            <p className="text-xs text-ink-muted">Solo se editan la insignia (con su enlace) y el video del botón "Ver demo".</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Insignia (badge)" value={hero.badge} onChange={(v) => update('hero', { ...hero, badge: v })} />
              <Field label="Enlace de la insignia" value={hero.badgeHref} onChange={(v) => update('hero', { ...hero, badgeHref: v })} />
            </div>
            <Field label='Video del botón "Ver demo" (URL de YouTube)' value={hero.demoVideoUrl} onChange={(v) => update('hero', { ...hero, demoVideoUrl: v })} />
          </>
        )

      case 'growingBusinesses':
        return (
          <>
            <AddBar onClick={() => update('growingBusinesses', [...growingBusinesses, { id: newId(), name: 'Nuevo negocio', logo: '' }])} />
            <div className="grid gap-3 sm:grid-cols-2">
              {growingBusinesses.map((b, i) => (
                <div key={b.id} className="rounded-lg border border-line p-3 space-y-3">
                  <ItemHead label={`Negocio ${i + 1}`} onRemove={() => update('growingBusinesses', growingBusinesses.filter((x) => x.id !== b.id))} />
                  <ImageField label="Logo" value={b.logo} maxDim={200} aspect="aspect-square" onChange={(v) => update('growingBusinesses', patch(growingBusinesses, b.id, { logo: v }))} />
                  <Field label="Nombre" value={b.name} onChange={(v) => update('growingBusinesses', patch(growingBusinesses, b.id, { name: v }))} />
                </div>
              ))}
            </div>
          </>
        )

      case 'businessTypes':
        return (
          <>
            <AddBar onClick={() => update('businessTypes', [...businessTypes, { id: newId(), iconKey: 'building', iconSvg: '', label: 'Nueva categoría', tagline: '', description: '', children: [], banner: '', link: '/registro', faqs: [], testimonial: { quote: '', name: '', role: '' } }])} />
            <div className="space-y-4">
              {businessTypes.map((t, i) => (
                <div key={t.id} className="rounded-lg border border-line p-4 space-y-3">
                  <ItemHead label={`Categoría ${i + 1}`} onRemove={() => update('businessTypes', businessTypes.filter((x) => x.id !== t.id))} />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ImageField label="Ícono (PNG o WebP)" value={t.iconSvg ?? ''} accept={ICON_ACCEPT} aspect="aspect-square" onChange={(v) => update('businessTypes', patch(businessTypes, t.id, { iconSvg: v }))} />
                    <Field label="Título" value={t.label} onChange={(v) => update('businessTypes', patch(businessTypes, t.id, { label: v }))} />
                  </div>
                  <Field label="Tagline" value={t.tagline} onChange={(v) => update('businessTypes', patch(businessTypes, t.id, { tagline: v }))} />
                  <Field label="Descripción" textarea value={t.description} onChange={(v) => update('businessTypes', patch(businessTypes, t.id, { description: v }))} />
                  <Field label="Etiquetas (separadas por coma)" value={t.children.join(', ')} onChange={(v) => update('businessTypes', patch(businessTypes, t.id, { children: splitList(v) }))} />
                  <ImageField label="Imagen" value={t.banner} aspect="aspect-[4/5]" onChange={(v) => update('businessTypes', patch(businessTypes, t.id, { banner: v }))} />
                  <Field label="Enlace (imagen y botón redirigen aquí)" value={t.link} onChange={(v) => update('businessTypes', patch(businessTypes, t.id, { link: v }))} />

                  {/* Desplegables (FAQs) */}
                  <div className="rounded-lg border border-line/70 bg-surface p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-ink-muted">Desplegables (preguntas)</span>
                      <button
                        onClick={() => update('businessTypes', patch(businessTypes, t.id, { faqs: [...t.faqs, { q: '', a: '' }] }))}
                        className="inline-flex items-center gap-1 rounded-lg bg-brand px-2 py-1 text-[11px] font-semibold text-white hover:bg-brand-dark"
                      >
                        <Plus className="size-3" /> Agregar
                      </button>
                    </div>
                    {t.faqs.map((f, fi) => (
                      <div key={fi} className="rounded-md border border-line bg-white p-2 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-semibold text-ink-subtle">#{fi + 1}</span>
                          <button onClick={() => update('businessTypes', patch(businessTypes, t.id, { faqs: t.faqs.filter((_, k) => k !== fi) }))} className="text-ink-subtle hover:text-danger" title="Eliminar">
                            <Trash2 className="size-3.5" />
                          </button>
                        </div>
                        <Field label="Pregunta" value={f.q} onChange={(v) => update('businessTypes', patch(businessTypes, t.id, { faqs: t.faqs.map((x, k) => (k === fi ? { ...x, q: v } : x)) }))} />
                        <Field label="Respuesta" textarea value={f.a} onChange={(v) => update('businessTypes', patch(businessTypes, t.id, { faqs: t.faqs.map((x, k) => (k === fi ? { ...x, a: v } : x)) }))} />
                      </div>
                    ))}
                  </div>

                  {/* Testimonio (debajo de la imagen en el landing) */}
                  <div className="rounded-lg border border-line/70 bg-surface p-3 space-y-2">
                    <span className="text-xs font-semibold text-ink-muted">Testimonio (se muestra debajo de la imagen)</span>
                    <Field label="Cita" textarea value={t.testimonial.quote} onChange={(v) => update('businessTypes', patch(businessTypes, t.id, { testimonial: { ...t.testimonial, quote: v } }))} />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Nombre" value={t.testimonial.name} onChange={(v) => update('businessTypes', patch(businessTypes, t.id, { testimonial: { ...t.testimonial, name: v } }))} />
                      <Field label="Cargo / lugar" value={t.testimonial.role} onChange={(v) => update('businessTypes', patch(businessTypes, t.id, { testimonial: { ...t.testimonial, role: v } }))} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )

      case 'carousel':
        return (
          <>
            <AddBar onClick={() => update('carousel', [...carousel, { id: newId(), image: '', link: '' }])} />
            {carousel.length === 0 && <p className="text-xs text-ink-muted">Sin diapositivas. Si no agregas ninguna, el carrusel no se muestra.</p>}
            <div className="grid gap-3 sm:grid-cols-2">
              {carousel.map((c, i) => (
                <div key={c.id} className="rounded-lg border border-line p-3 space-y-3">
                  <ItemHead label={`Diapositiva ${i + 1}`} onRemove={() => update('carousel', carousel.filter((x) => x.id !== c.id))} />
                  <ImageField label="Imagen" value={c.image} aspect="aspect-video" onChange={(v) => update('carousel', patch(carousel, c.id, { image: v }))} />
                  <Field label="Enlace al hacer clic" value={c.link} onChange={(v) => update('carousel', patch(carousel, c.id, { link: v }))} />
                </div>
              ))}
            </div>
          </>
        )

      case 'ecosystem':
        return (
          <>
            <p className="text-xs text-ink-muted">Cada módulo aparece en la órbita; al pasar el cursor se abre un modal con su descripción. El botón es opcional (solo si pones texto y enlace).</p>
            <AddBar onClick={() => update('ecosystem', [...ecosystem, { id: newId(), iconKey: 'package', iconSvg: '', label: 'Nuevo módulo', description: '', buttonLabel: '', buttonHref: '' }])} />
            <div className="grid gap-3 sm:grid-cols-2">
              {ecosystem.map((m, i) => (
                <div key={m.id} className="rounded-lg border border-line p-3 space-y-3">
                  <ItemHead label={`Módulo ${i + 1}`} onRemove={() => update('ecosystem', ecosystem.filter((x) => x.id !== m.id))} />
                  <ImageField label="Ícono (PNG o WebP)" value={m.iconSvg ?? ''} accept={ICON_ACCEPT} aspect="aspect-square" onChange={(v) => update('ecosystem', patch(ecosystem, m.id, { iconSvg: v }))} />
                  <Field label="Nombre" value={m.label} onChange={(v) => update('ecosystem', patch(ecosystem, m.id, { label: v }))} />
                  <Field label="Descripción" textarea value={m.description} onChange={(v) => update('ecosystem', patch(ecosystem, m.id, { description: v }))} />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Texto del botón (opcional)" value={m.buttonLabel} onChange={(v) => update('ecosystem', patch(ecosystem, m.id, { buttonLabel: v }))} />
                    <Field label="Enlace del botón" value={m.buttonHref} onChange={(v) => update('ecosystem', patch(ecosystem, m.id, { buttonHref: v }))} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )

      case 'pricing':
        return (
          <>
            <AddBar onClick={() => update('pricing', [...pricing, { id: newId(), name: 'Nuevo plan', tag: '', price: 'Desde', priceMonthly: '', priceAnnual: '', annualBadge: '', annualOnly: false, features: [], popular: false, ctaLabel: 'Solicitar', ctaHref: '/registro' }])} />
            <div className="space-y-4">
              {pricing.map((p, i) => (
                <div key={p.id} className={`rounded-lg border p-4 space-y-3 ${p.popular ? 'border-brand bg-brand-soft/30' : 'border-line'}`}>
                  <ItemHead label={`Plan ${i + 1}`} onRemove={() => update('pricing', pricing.filter((x) => x.id !== p.id))} />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Nombre" value={p.name} onChange={(v) => update('pricing', patch(pricing, p.id, { name: v }))} />
                    <Field label="Etiqueta (ej. Ideal para empezar)" value={p.tag} onChange={(v) => update('pricing', patch(pricing, p.id, { tag: v }))} />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label="Precio mensual (ej. $49)" value={p.priceMonthly} onChange={(v) => update('pricing', patch(pricing, p.id, { priceMonthly: v }))} />
                    <Field label="Precio anual (por mes, ej. $39)" value={p.priceAnnual} onChange={(v) => update('pricing', patch(pricing, p.id, { priceAnnual: v }))} />
                    <Field label="Etiqueta de ahorro (ej. Ahorra 20%)" value={p.annualBadge} onChange={(v) => update('pricing', patch(pricing, p.id, { annualBadge: v }))} />
                  </div>
                  <Field label="Precio de respaldo (si no hay mensual/anual, ej. Cotización personalizada)" value={p.price} onChange={(v) => update('pricing', patch(pricing, p.id, { price: v }))} />
                  <Field label="Características (una por línea)" textarea value={p.features.join('\n')} onChange={(v) => update('pricing', patch(pricing, p.id, { features: splitLines(v) }))} />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Texto del botón" value={p.ctaLabel} onChange={(v) => update('pricing', patch(pricing, p.id, { ctaLabel: v }))} />
                    <Field label="Enlace del botón" value={p.ctaHref} onChange={(v) => update('pricing', patch(pricing, p.id, { ctaHref: v }))} />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-ink">
                    <input
                      type="radio"
                      name="popular-plan"
                      checked={p.popular}
                      onChange={() => update('pricing', pricing.map((x) => ({ ...x, popular: x.id === p.id })))}
                      className="accent-[color:var(--brand-orange)]"
                    />
                    Marcar como plan más popular
                  </label>
                  <label className="flex items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={!!p.annualOnly}
                      onChange={(e) => update('pricing', patch(pricing, p.id, { annualOnly: e.target.checked }))}
                      className="accent-[color:var(--brand-orange)]"
                    />
                    Solo contratación anual (la card muestra siempre el precio anual, aunque el visitante elija "Mensual")
                  </label>
                </div>
              ))}
            </div>
          </>
        )

      case 'downloads': {
        // El editor es la herramienta con la que se ARREGLA un documento roto:
        // si se cae con uno, no queda forma de repararlo desde el panel. Y el
        // documento llega de un `JSON.parse(...) as LandingContent` que no
        // valida nada. Así que aquí se lee a la defensiva.
        const seccion = {
          title: typeof downloads?.title === 'string' ? downloads.title : '',
          intro: typeof downloads?.intro === 'string' ? downloads.intro : '',
          pvDesktopChannelUrl:
            typeof downloads?.pvDesktopChannelUrl === 'string' ? downloads.pvDesktopChannelUrl : '',
          apps: Array.isArray(downloads?.apps)
            ? downloads.apps.filter((a) => a && typeof a === 'object')
            : [],
        }
        return (
          <>
            <p className="text-xs text-ink-muted">
              Se enseñan en el orden de esta lista. Cada tarjeta solo pinta los botones que tienen
              enlace: una app que aún no está en la App Store no tiene insignia de App Store. Los
              archivos .apk y .exe <strong>no se suben aquí</strong>: se pega el enlace público
              donde ya están alojados (hoy, repo.tecopos.com).
            </p>
            <Field label="Título de la página" value={seccion.title} onChange={(v) => update('downloads', { ...seccion, title: v })} />
            <Field label="Párrafo de entrada" textarea value={seccion.intro} onChange={(v) => update('downloads', { ...seccion, intro: v })} />
            <div>
              <Field label="Carpeta del canal de TECOPOS escritorio" value={seccion.pvDesktopChannelUrl} onChange={(v) => update('downloads', { ...seccion, pvDesktopChannelUrl: v })} />
              <p className="mt-1 text-xs text-ink-muted">
                La tarjeta del punto de venta para Windows toma la versión y el instalador del{' '}
                <code>latest.yml</code> de esta carpeta. Vacía, se usa
                https://repo.tecopos.com/PV_Desktop/. Su enlace de Windows queda de respaldo para
                cuando el canal no responde.
              </p>
            </div>
            <AddBar onClick={() => update('downloads', { ...seccion, apps: [...seccion.apps, { id: newId(), name: 'Nueva aplicación', tagline: '', description: '', icon: '', platforms: ['android'], playStoreUrl: '', appStoreUrl: '', apkUrl: '', apkVersion: '', windowsUrl: '', windowsVersion: '', visible: true }] })} />
            <div className="space-y-4">
              {seccion.apps.map((a, i) => (
                <div key={a.id} className="rounded-lg border border-line p-3 space-y-3">
                  <ItemHead label={`Aplicación ${i + 1}`} onRemove={() => update('downloads', { ...seccion, apps: seccion.apps.filter((x) => x.id !== a.id) })} />
                  <ImageField label="Ícono (cuadrado)" value={a.icon} aspect="aspect-square" onChange={(v) => update('downloads', { ...seccion, apps: patch(seccion.apps, a.id, { icon: v }) })} />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Nombre" value={a.name} onChange={(v) => update('downloads', { ...seccion, apps: patch(seccion.apps, a.id, { name: v }) })} />
                    <Field label="Frase corta" value={a.tagline} onChange={(v) => update('downloads', { ...seccion, apps: patch(seccion.apps, a.id, { tagline: v }) })} />
                  </div>
                  <Field label="Descripción" textarea value={a.description} onChange={(v) => update('downloads', { ...seccion, apps: patch(seccion.apps, a.id, { description: v }) })} />
                  <div>
                    <span className="text-xs font-semibold text-ink-muted">Plataformas (etiquetas de la tarjeta y filtro de la página)</span>
                    <div className="mt-1.5 flex flex-wrap gap-3">
                      {(Object.keys(DOWNLOAD_PLATFORM_LABELS) as DownloadPlatform[]).map((p) => (
                        <label key={p} className="inline-flex items-center gap-1.5 text-sm text-ink">
                          <input
                            type="checkbox"
                            checked={a.platforms.includes(p)}
                            onChange={(e) => update('downloads', { ...seccion, apps: patch(seccion.apps, a.id, { platforms: e.target.checked ? [...a.platforms, p] : a.platforms.filter((x) => x !== p) }) })}
                            className="size-4 accent-[color:var(--brand-orange)]"
                          />
                          {DOWNLOAD_PLATFORM_LABELS[p]}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Google Play (enlace)" value={a.playStoreUrl} onChange={(v) => update('downloads', { ...seccion, apps: patch(seccion.apps, a.id, { playStoreUrl: v }) })} />
                    <Field label="App Store (enlace)" value={a.appStoreUrl} onChange={(v) => update('downloads', { ...seccion, apps: patch(seccion.apps, a.id, { appStoreUrl: v }) })} />
                    <Field label="APK (enlace de descarga)" value={a.apkUrl} onChange={(v) => update('downloads', { ...seccion, apps: patch(seccion.apps, a.id, { apkUrl: v }) })} />
                    <Field label="Versión del APK" value={a.apkVersion} onChange={(v) => update('downloads', { ...seccion, apps: patch(seccion.apps, a.id, { apkVersion: v }) })} />
                    <Field label="Windows (enlace del instalador)" value={a.windowsUrl} onChange={(v) => update('downloads', { ...seccion, apps: patch(seccion.apps, a.id, { windowsUrl: v }) })} />
                    <Field label="Versión de Windows" value={a.windowsVersion} onChange={(v) => update('downloads', { ...seccion, apps: patch(seccion.apps, a.id, { windowsVersion: v }) })} />
                  </div>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" checked={a.visible} onChange={(e) => update('downloads', { ...seccion, apps: patch(seccion.apps, a.id, { visible: e.target.checked }) })} className="size-4 accent-[color:var(--brand-orange)]" />
                    <span className="text-sm text-ink">Visible en la página</span>
                  </label>
                </div>
              ))}
            </div>
          </>
        )
      }

      case 'contact':
        return (
          <>
            <p className="text-xs text-ink-muted">Número institucional al que redirige el botón de WhatsApp del Asistente. Escríbelo con código de país y sin signos (ej. 5350000000).</p>
            <Field label="WhatsApp institucional" value={contact.whatsapp} onChange={(v) => update('contact', { ...contact, whatsapp: v })} />
            <Field label="Mensaje predefinido" textarea value={contact.whatsappMessage} onChange={(v) => update('contact', { ...contact, whatsappMessage: v })} />
            <Field label='Enlace de Tecobot (botón flotante "Preguntar a Tecobot")' value={contact.tecobotUrl} onChange={(v) => update('contact', { ...contact, tecobotUrl: v })} />
          </>
        )

      case 'social':
        return (
          <>
            <p className="text-xs text-ink-muted">Enlaces de las redes sociales. Se muestran en el pie del sitio. Deja en blanco las que no uses.</p>
            <Field label="Canal de WhatsApp" value={social.whatsappChannel} onChange={(v) => update('social', { ...social, whatsappChannel: v })} />
            <Field label="YouTube" value={social.youtube} onChange={(v) => update('social', { ...social, youtube: v })} />
            <Field label="Facebook" value={social.facebook} onChange={(v) => update('social', { ...social, facebook: v })} />
            <Field label="Instagram" value={social.instagram} onChange={(v) => update('social', { ...social, instagram: v })} />
          </>
        )

      default:
        return null
    }
  }
}

/* ---------- helpers ---------- */
function patch<T extends { id: string }>(arr: T[], id: string, p: Partial<T>): T[] {
  return arr.map((it) => (it.id === id ? { ...it, ...p } : it))
}
function splitList(v: string): string[] {
  return v.split(',').map((s) => s.trim()).filter(Boolean)
}
function splitLines(v: string): string[] {
  return v.split('\n').map((s) => s.trim()).filter(Boolean)
}

function ItemHead({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-ink-muted">{label}</span>
      <button onClick={onRemove} className="text-ink-subtle hover:text-danger" title="Eliminar">
        <Trash2 className="size-4" />
      </button>
    </div>
  )
}

function AddBar({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-dark">
      <Plus className="size-3.5" /> Agregar
    </button>
  )
}

function Field({ label, value, onChange, textarea }: { label: string; value: string; onChange: (v: string) => void; textarea?: boolean }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-ink-muted">{label}</span>
      {textarea ? (
        <textarea value={value} rows={3} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full resize-y rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand" />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand" />
      )}
    </label>
  )
}

